import ProfileCreationIcon from "@assets/icons/calendar-check.svg";
import MedicalConditionIcon from "@assets/icons/treatment.svg";
import PatientInformationIcon from "@assets/icons/user.svg";
import FunctionalStatusIcon from "@assets/icons/walking.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { healthcareTeamManager } from "@core/service-container";
import { SharePatientOutput } from "@domain/healthcare-team/healthcare-team";
import {
	sendAnalyticsEventAcceptInvitation,
	sendAnalyticsEventCancelInvitation,
} from "@domain/healthcare-team/healthcare-team-analytics";
import { useFormatMedicalCondition } from "@domain/medical-condition/use-medical-condition";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ToastContext } from "@ui/components/common/toast-provider";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { PatientDetailItem } from "@ui/pages/home/dialog/components/patient-detail-item";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotEmpty, isNotNil } from "@utils/assert";
import { formatName } from "@utils/string-utils";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useMemo } from "react";

export interface HealthcareTeamAcceptDialogProps extends DialogProps {
	healthcareTeamOutput: SharePatientOutput;
}

export const HealthcareTeamAcceptDialog = ({
	healthcareTeamOutput: { invitation, patient },
	...otherProps
}: HealthcareTeamAcceptDialogProps): JSX.Element => {
	const { format, formatAge, formatDateMed } = useI18n();
	const { formatFunctionalStatus, formatDisease } = useFormatMedicalCondition();
	const navigate = useNavigate();
	const { showToast } = useContext(ToastContext);

	const patientNameAndAge = useMemo(() => {
		const name = formatName(patient);
		if (isNotEmpty(name)) {
			const age = isNotNil(patient.birthDate) ? formatAge(patient.birthDate) : undefined;
			if (isNotNil(age)) {
				return [name, age].join(", ");
			}
			return name;
		}
		return format("global.empty");
	}, [format, formatAge, patient]);

	const [acceptInvitation, isLoading] = useAsyncOperation(async () => {
		try {
			await healthcareTeamManager.confirmInvitation(patient.id);
			sendAnalyticsEventAcceptInvitation(patient);
			otherProps.onHide();
			showToast(
				format("patientDetails.healthcareTeam.toastJoinSuccess.title"),
				format("patientDetails.healthcareTeam.toastJoinSuccess.subtitle", { patientName: formatName(patient) }),
				"success",
			);
			navigate(RoutesPath.PatientDetails, {
				route: { patientId: patient.id },
				query: { active: TabPatientDetails.PROGRAMS_TAB },
			});
		} catch (e) {
			console.error("Failed to accept healthcare team invite", e);
		}
	}, {});

	const cancelInvitation = useCallback(() => {
		sendAnalyticsEventCancelInvitation(patient);
		otherProps.onHide();
	}, [otherProps, patient]);

	return (
		<BaseDialog
			header={format("home.healthcareTeam.acceptDialog.header")}
			footer={
				<ButtonContainer>
					<Button uiStyle="secondary" onClick={cancelInvitation} label={format("global.dialog.cancel")} />
					<Button
						uiStyle="primary"
						onClick={acceptInvitation}
						loading={isLoading}
						label={format("home.healthcareTeam.acceptDialog.acceptButton")}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.L}
			{...otherProps}
		>
			<ContentContainer>
				<div>
					{format("home.healthcareTeam.acceptDialog.description.v1_6", {
						ptName: formatName(invitation.invitingPt),
						patientName: formatName(patient),
					})}
				</div>

				{invitation.message && (
					<Container>
						<Message>{`“${invitation.message}”`}</Message>
					</Container>
				)}

				<div className="p-size-l-bold">{format("home.healthcareTeam.acceptDialog.patientDetails.header")}</div>

				<PatientDetailsContainer>
					<PatientDetailItem
						icon={PatientInformationIcon}
						title={format("home.healthcareTeam.acceptDialog.patientDetails.patientInformation")}
						subtitle={patientNameAndAge}
					/>
					<PatientDetailItem
						icon={MedicalConditionIcon}
						title={format("home.healthcareTeam.acceptDialog.patientDetails.medicalCondition")}
						subtitle={formatDisease(patient.disease)}
					/>
					<PatientDetailItem
						icon={ProfileCreationIcon}
						title={format("home.healthcareTeam.acceptDialog.patientDetails.profileCreation")}
						subtitle={formatDateMed(patient.creationDate)}
					/>
					<PatientDetailItem
						icon={FunctionalStatusIcon}
						title={format("home.healthcareTeam.acceptDialog.patientDetails.functionalStatus")}
						subtitle={formatFunctionalStatus(patient.functionalStatus)}
					/>
				</PatientDetailsContainer>
			</ContentContainer>
		</BaseDialog>
	);
};

const ContentContainer = styled.div`
	${stack(24, "center")};
`;

const Message = styled.div`
	white-space: pre-line;
`;

const Container = styled.div`
	background-color: ${colors.primaryBackground};
	padding: 16px;
	border-radius: 8px;
`;

const PatientDetailsContainer = styled(Container)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 24px;
	align-self: end;
`;
